import React, { useState } from 'react';
import {
  CcfBox,
  CcfHeader,
  CcfInteractionSearchIcon,
  CcfTabs,
  useTranslator,
} from '@nice-ccf/ui-controls';
import styles from './ccf-interaction-search.module.scss';
import { makeStyles } from '@mui/styles';
import {
  Box,
  Direction,
  FormControl,
  MenuItem,
  Select,
  useMediaQuery,
} from '@mui/material';
import { Theme, useTheme } from '@mui/material';
import { useSelector } from 'react-redux';
import createStyles from '@mui/styles/createStyles';
import { getApplicationDirection } from '../../global.app.slice';
import CcfInteractionSearchCasesTab from './ccf-interaction-search-cases-tab/ccf-interaction-search-cases-tab/ccf-interaction-search-cases-tab';
import CcfInteractionSearchMessagesTab from './ccf-interaction-search-messages-tab/ccf-interaction-search-messages-tab/ccf-interaction-search-messages-tab';
import CcfInteractionSearchThreadsTab from './ccf-interaction-search-threads-tab/ccf-interaction-search-threads-tab/ccf-interaction-search-threads-tab';
import CcfInteractionSearchCustomersTab from './ccf-interaction-search-customers-tab/ccf-interaction-search-customers-tab/ccf-interaction-search-customers-tab';
// import CcfInteractionSearchThreadsTab from './ccf-interaction-search-threads-tab/ccf-interaction-search-threads-tab/ccf-interaction-search-threads-tab';
// import CcfInteractionSearchCustomersTab from './ccf-interaction-search-customers-tab/ccf-interaction-search-customers-tab/ccf-interaction-search-customers-tab';

/* eslint-disable-next-line */
export interface CcfInteractionSearchProps {}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      '& .MuiDataGrid-root': {
        border: 'none',
        height: '100%',
      },
      '& .MuiDataGrid-columnHeaderWrapper': {
        height: '36px',
      },
      '& .MuiDataGrid-columnHeaders': {
        border: '1px solid #d2d8db',
        borderRadius: '0px',
      },
      '& .MuiDataGrid-virtualScrollerContent': {
        height: '1px !important',
      },
      '& .MuiDataGrid-columnHeaderTitleContainer': {
        padding: '0px',
        fontFamily: 'Open Sans',
        fontStyle: 'normal',
        fontWeight: 600,
        fontSize: '12px',
        lineHeight: '16px',
        textTransform: 'uppercase',
        color: '#526b7a',
      },
      '& .MuiDataGrid-columnHeaderTitle': {
        fontWeight: 600,
      },
      '& .MuiDataGrid-columnsContainer': {
        background: '#FFFFF',
        border: '1px solid #D2D8DB',
        boxShadow: '0px 3px 4px rgba(0, 0, 0, 0.08)',
      },
      '& .styledrows': {
        height: '36px',
        background: '#FFFFF',
        border: '1px solid #D2D8DB',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        fontFamily: 'Open Sans',
        fontStyle: 'normal',
        fontWeight: '400',
        fontSize: '13px',
        lineHeight: '18px',
        color: '#333333',
      },
    },
    searchInput: {
      '&.MuiOutlinedInput-inputAdornedEnd': {
        paddingBottom: '7.5px',
      },
    },
    interactionSearchCard: {
      width: '100%',
      height: '100%',
      background: `${theme.palette.background.light} 0% 0% no-repeat padding-box`,
      boxShadow: `0px 1px 3px ${theme.palette.boxshadow.main}`,
      border: `1px solid ${theme.palette.border.main}`,
      borderRadius: '6px',
      opacity: 1,
      marginRight: '12px',
      transition: 'all 0.5s ease',
      display: 'flex',
      flexDirection: 'column',
      '& .MuiOutlinedInput-input': {
        ...theme.typography.h4,
      },
    },
    interactionSearchHeader: {
      background: `${theme.palette.background.level1} 0% 0% no-repeat padding-box`,
      borderRadius: '5px 5px 0px 0px',
      opacity: 1,
      display: 'flex',
      alignItems: 'center',
      paddingTop: '10px',
      paddingBottom: '10px',
      paddingLeft: '5px',
      marginTop: '0px !important',
      '& section': {
        marginBottom: '0px !important',
        alignItems: 'center',
      },
      '& > div': {
        marginTop: '0px !important',
      },
      '& label': {
        color: theme.palette.text.secondary,
        marginLeft: '4px !important',
        marginTop: '0px !important',
        fontWeight: 'bold',
        lineHeight: '1',
      },
      '& svg': {
        verticalAlign: 'middle',
      },
    },
  })
);

/**
 * CcfInteractionSearch - used to display Notes in contact card
 * @param props -? - CcfInteractionSearchProps
 * @example - <CcfInteractionSearch />
 */
export function CcfInteractionSearch(props: CcfInteractionSearchProps) {
  const classes = useStyles();
  const appDirection: Direction = useSelector(getApplicationDirection);
  const [translate] = useTranslator();
  const theme: Theme = useTheme();

  const isSmView = useMediaQuery(theme.breakpoints.down('xl'), { noSsr: true });

  const interactionTabs = [
    {
      element: <CcfInteractionSearchCasesTab />,
      tooltip: translate('interactions'),
      isActive: true,
      menuName: translate('interactions'),
      id: 0,
    },
    {
      element: <CcfInteractionSearchMessagesTab />,
      tooltip: translate('messages'),
      isActive: false,
      menuName: translate('messages'),
      id: 1,
    },
    {
      element: <CcfInteractionSearchCustomersTab />,
      tooltip: translate('customers'),
      isActive: true,
      menuName: translate('customers'),
      id: 2,
    },
    {
      element: <CcfInteractionSearchThreadsTab />,
      tooltip: translate('threads'),
      isActive: true,
      menuName: translate('threads'),
      id: 3,
    },
  ];
  const [currentTab, setCurrentTab] = useState(0);

  /**
   * Used to update the app space tab menuName and tab index
   * @param newTabIndex - number
   * @example - handleChange(0)
   */
  const handleChange = (newTabIndex: number) => {
    setCurrentTab(newTabIndex);
  };

  return (
    <section className={classes.interactionSearchCard}>
      <CcfBox
        id="agent_interactionSearchHeader"
        className={classes.interactionSearchHeader}
      >
        <CcfHeader
          LeftIcon={<CcfInteractionSearchIcon fillColor={'#2D5651'} />}
          headerText={translate('search')}
          direction={appDirection}
        />
      </CcfBox>

      {isSmView && (
        <CcfBox>
          <div
            style={{ borderBottom: '1px solid #dde4e9' }}
            id="agent_consumerCard_minView_dropdownDiv"
          >
            <FormControl
              variant="standard"
              sx={{
                margin: '10px 0px 0px 10px',
                borderBottom: '2px solid #005C99',
              }}
            >
              <Select
                variant="standard"
                value={interactionTabs.filter(
                  (tab) => tab.id === currentTab
                )[0].menuName}
                onChange={(e: any) => {
                  const getIndex = interactionTabs.findIndex(
                    (tab) => tab.menuName === e.target.value
                  );

                  handleChange(getIndex);
                }}
                className={styles.customDropdown}
                id="agent_interactionSearch_minView_dropdown"
              >
                {interactionTabs.map((key: any) => (
                  <MenuItem
                    id={`agent_interactionSearch_minView_dropdown_${key.menuName}`}
                    key={key.menuName}
                    value={key.menuName}
                  >
                    {key.menuName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </CcfBox>
      )}
      <CcfBox
        id="agent_interactionSearchTabContainer"
        style={{ height: 'calc(100% - 33px)' }}
      >
        <CcfTabs
          currentTab={currentTab}
          textColor={CcfTabs.Color.PRIMARY}
          variant={CcfTabs.Variant.STANDARD}
          setCurrentTab={handleChange}
          classes={{
            root: styles.tabsContainer,
            indicator: styles.activeTabIndicator,
          }}
          id={`agent_interactionSearch_${currentTab}_tab`}
          sx={isSmView ? { display: 'None' } : { display: 'flex' }}
          aria-label="interaction search tabs"
        >
          {interactionTabs.map((tab, index) => (
            <CcfTabs.TabPanel key={index} label={tab.menuName}>
              <Box
                id={`agent_interactionSearch_${tab.element}_tab`}
                style={{ height: 'calc(100% - 40px)' }}
              >
                {tab.element}
              </Box>
            </CcfTabs.TabPanel>
          ))}
        </CcfTabs>
      </CcfBox>
    </section>
  );
}

export default CcfInteractionSearch;
